<template>
  <section class="oc-card" :class="{ 'oc-card--hl': hovered !== null }" data-test="ti-outcome-ring-card">
    <header class="oc-head">
      <div>
        <h3 class="oc-title">{{ title }}</h3>
        <p class="oc-sub">{{ subtitle }}</p>
      </div>
      <button
        type="button"
        class="oc-ghost"
        data-test="ti-ring-table-toggle"
        :aria-expanded="showTable ? 'true' : 'false'"
        aria-label="Show or hide the data table"
        @click="showTable = !showTable"
      >
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><circle cx="5" cy="12" r="1.9" fill="currentColor" /><circle cx="12" cy="12" r="1.9" fill="currentColor" /><circle cx="19" cy="12" r="1.9" fill="currentColor" /></svg>
      </button>
    </header>

    <StatTiles
      v-if="stats.length"
      class="oc-stats"
      :stats="stats"
      openable
      :active-id="openChip"
      :disabled-ids="emptyChipIds"
      @open="toggleChip"
    />
    <div v-else class="oc-hero">
      <span class="oc-num" data-test="ti-ring-total">{{ model.total }}</span>
      <span class="oc-of">{{ unit }} logged</span>
    </div>
    <p v-if="stats.length && statsNote" class="oc-stats-note" data-test="ti-stats-note">{{ statsNote }}</p>

    <div v-if="goal" class="oc-goal" data-test="ti-goal">
      <div class="oc-goal-row">
        <span class="oc-goal-met"><b>{{ goal.percent }}%</b> of {{ goal.word }} Goal Met</span>
        <span class="oc-goal-target">{{ goal.word }} Goal: {{ goal.target }} follow-ups</span>
      </div>
      <div class="oc-goal-track" role="img" :aria-label="`${goal.percent}% of the ${goal.word.toLowerCase()} goal of ${goal.target} follow-ups met`">
        <i class="oc-goal-fill" :style="{ width: goal.fill + '%' }"></i>
      </div>
    </div>

    <div v-if="chips.length && !stats.length" class="oc-chips">
      <button
        v-for="c in chips"
        :key="c.id"
        type="button"
        class="oc-chip"
        :class="{ 'is-open': openChip === c.id }"
        :disabled="c.rows.length === 0"
        :aria-expanded="openChip === c.id ? 'true' : 'false'"
        :data-test="`ti-chip-${c.id}`"
        @click="toggleChip(c.id)"
      >
        <template v-if="c.kind === 'value'"><span class="oc-chip-k">{{ c.label }}</span><b>{{ c.count }}</b> {{ c.noun }}<span v-if="c.value" class="oc-chip-v">{{ c.value }}</span></template>
        <template v-else>{{ c.label }}</template>
        <svg v-if="c.rows.length" class="oc-chip-caret" viewBox="0 0 12 12" width="10" height="10" aria-hidden="true"><path d="M2 4.5 6 8.5 10 4.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg>
      </button>
    </div>
    <div v-if="openChipData" class="oc-list" data-test="ti-chip-list">
      <p class="oc-list-title">{{ openChipData.title }}</p>
      <div class="oc-list-scroll">
        <table>
          <thead>
            <tr><th v-for="(col, i) in openChipData.columns" :key="i" scope="col">{{ col }}</th></tr>
          </thead>
          <tbody>
            <tr v-for="(row, r) in openChipData.rows" :key="r"><td v-for="(cell, i) in row" :key="i">{{ cell }}</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="oc-body">
      <div class="oc-ring">
        <svg :viewBox="`0 0 ${SIZE} ${SIZE}`" class="oc-svg" role="img" :aria-label="ringLabel">
          <path
            v-for="(p, i) in petals"
            :key="i"
            class="oc-petal"
            :class="{ 'is-dim': p.key !== null && isDim(p.key) }"
            :style="{ fill: p.color, stroke: p.color }"
            :d="p.d"
          ><title v-if="p.title">{{ p.title }}</title></path>
        </svg>
        <div class="oc-center">
          <b data-test="ti-ring-center">{{ centerBig }}</b>
          <span>{{ centerSmall }}</span>
        </div>
      </div>

      <ul class="oc-legend" aria-label="Follow-ups by outcome">
        <li
          v-for="o in model.outcomes"
          :key="o.key"
          class="oc-row"
          :class="{ 'is-dim': isDim(o.key), 'is-zero': o.count === 0, 'is-button': o.isOther }"
          :title="tileTitle(o)"
          tabindex="0"
          data-test="ti-ring-tile"
          :role="o.isOther ? 'button' : null"
          :aria-expanded="o.isOther ? (otherOpen ? 'true' : 'false') : null"
          @mouseenter="hovered = o.key"
          @mouseleave="hovered = null"
          @focus="hovered = o.key"
          @blur="hovered = null"
          @click="o.isOther && (otherOpen = !otherOpen)"
          @keydown.enter="o.isOther && (otherOpen = !otherOpen)"
        >
          <i class="oc-dot" :style="{ background: o.color }" aria-hidden="true"></i>
          <span class="oc-row-label">{{ o.label }}<small v-if="o.isOther" class="oc-row-more">{{ o.folded.length }} outcomes</small><svg v-if="o.isOther" class="oc-row-caret" :class="{ 'is-open': otherOpen }" viewBox="0 0 12 12" width="10" height="10" aria-hidden="true"><path d="M2 4.5 6 8.5 10 4.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg></span>
          <b class="oc-row-n">{{ o.count }}</b>
          <span class="oc-row-p">{{ o.percent }}%</span>
        </li>
      </ul>
    </div>

    <div v-if="otherOpen && otherOutcome" class="oc-list" data-test="ti-other-list">
      <p class="oc-list-title">Inside "Other"</p>
      <div class="oc-list-scroll">
        <table>
          <thead><tr><th scope="col">Outcome</th><th scope="col">{{ unitCap }}</th></tr></thead>
          <tbody><tr v-for="f in otherOutcome.folded" :key="f.label"><td>{{ f.label }}</td><td>{{ f.count }}</td></tr></tbody>
        </table>
      </div>
    </div>

    <div v-if="showTable" class="oc-table" data-test="ti-ring-table">
      <table>
        <thead>
          <tr><th scope="col">Outcome</th><th scope="col">{{ unitCap }}</th><th scope="col">Share</th></tr>
        </thead>
        <tbody>
          <template v-for="o in model.outcomes">
            <tr :key="o.key">
              <th scope="row">{{ o.label }}</th><td>{{ o.count }}</td><td>{{ o.percent }}%</td>
            </tr>
            <tr v-for="f in (o.folded || [])" :key="`${o.key}-${f.label}`" class="oc-folded">
              <th scope="row">{{ f.label }}</th><td>{{ f.count }}</td><td>{{ pct(f.count) }}%</td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
import { PETALS, buildOutcomeModel, percent } from './outcomeModel.js';
import StatTiles from './StatTiles.vue';

const SIZE = 270;
const INNER = 78;
const OUTER = 120;
const GAP = 0.05;
const EMPTY_PETAL = '#e9edf0';

// The path of one petal: an arc segment between two radii.
function wedge(cx, ri, ro, a0, a1) {
  const p = (r, a) => `${(cx + r * Math.cos(a)).toFixed(2)},${(cx + r * Math.sin(a)).toFixed(2)}`;
  return `M${p(ro, a0)}A${ro},${ro} 0 0 1 ${p(ro, a1)}L${p(ri, a1)}A${ri},${ri} 0 0 0 ${p(ri, a0)}Z`;
}

/**
 * The team card (Team Insights redesign, "slim side by side"): the headline numbers, then a 50-petal ring beside a one-line-per-outcome
 * list. It is handed the SAME chartData the classic pie draws from, and shows only numbers already in it (outcomeModel.js). The petals
 * are each outcome's share of the follow-ups, 50 petals in all — it is not a progress bar. Where the site has a goal for the section
 * (Overdue Recall / Unscheduled Treatment on Today, Week and Month) the `goal` prop adds the classic "N% of Daily Goal Met" line and its bar.
 *
 * Two ways to show the headline numbers: `stats` (the four number tiles; a tile with a list behind it opens that list in place) or,
 * without `stats`, the big total with optional `chips` (the Today tab pilot).
 */
export default {
  name: 'OutcomeRingCard',
  components: { StatTiles },
  props: {
    chartData: { type: Object, default: null },
    title: { type: String, default: 'Patient Balances' },
    subtitle: { type: String, default: 'Follow-ups logged today' },
    unit: { type: String, default: 'follow-ups' },
    // [{ id, kind: 'calls' | 'value', label, count, noun, value, title, columns, rows }] — see chipRows.js. The lists the tiles / chips open in place.
    chips: { type: Array, default: () => [] },
    // [{ id, label, value, sub, opens }] — chipRows.js buildStats
    stats: { type: Array, default: () => [] },
    // one plain sentence under the tiles (chipRows.js buildUnassignedNote), '' when there is nothing to explain
    statsNote: { type: String, default: '' },
    // { word: 'Daily' | 'Weekly' | 'Monthly', target, percent, fill } — sectionHelpers.js tiGoal (the classic "N% of Daily Goal Met" line and bar); null = no goal
    goal: { type: Object, default: null },
  },
  data() {
    return { SIZE, hovered: null, showTable: false, openChip: null, otherOpen: false };
  },
  computed: {
    otherOutcome() {
      return this.model.outcomes.find(o => o.isOther) || null;
    },
    openChipData() {
      return this.chips.find(c => c.id === this.openChip && c.rows.length > 0) || null;
    },
    emptyChipIds() {
      return this.chips.filter(c => c.rows.length === 0).map(c => c.id);
    },
    model() {
      return buildOutcomeModel(this.chartData);
    },
    unitCap() {
      return this.unit.charAt(0).toUpperCase() + this.unit.slice(1);
    },
    petals() {
      const cx = SIZE / 2;
      const step = (2 * Math.PI) / PETALS;
      const seq = [];
      this.model.outcomes.forEach((o) => {
        for (let j = 0; j < o.petals; j += 1) seq.push(o);
      });
      while (seq.length < PETALS) seq.push(null);
      return seq.map((o, n) => ({
        key: o ? o.key : null,
        color: o ? o.color : EMPTY_PETAL,
        title: o ? `${o.label}: ${o.count} (${o.percent}%)` : '',
        d: wedge(cx, INNER + 2.5, OUTER - 2.5, -Math.PI / 2 + n * step + GAP, -Math.PI / 2 + (n + 1) * step - GAP),
      }));
    },
    hoveredOutcome() {
      return this.model.outcomes.find(o => o.key === this.hovered) || null;
    },
    centerBig() {
      return this.hoveredOutcome ? this.hoveredOutcome.count : this.model.total;
    },
    centerSmall() {
      return this.hoveredOutcome ? this.hoveredOutcome.label : this.unit;
    },
    ringLabel() {
      return `${this.model.total} ${this.unit}, coloured by outcome: ${this.model.outcomes.map(o => `${o.label} ${o.count}`).join(', ')}. A data table is available.`;
    },
  },
  methods: {
    toggleChip(id) {
      this.openChip = this.openChip === id ? null : id;
    },
    isDim(key) {
      return this.hovered !== null && this.hovered !== key;
    },
    pct(n) {
      return percent(n, this.model.total);
    },
    // "Other" lists what it holds, so no outcome is hidden behind the grey row.
    tileTitle(o) {
      if (!o.isOther) return `${o.label}: ${o.count} (${o.percent}%)`;
      return `${o.label}: ${o.count} (${o.percent}%) — ${o.folded.filter(f => f.count > 0).map(f => `${f.label} ${f.count}`).join(', ') || 'none yet'}`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../components/Followups/mixins';

.oc-card {
  @include fu-font-base;
  background: $fu-surface;
  border: 1px solid $fu-border;
  border-radius: 28px;
  padding: 22px;
  box-shadow: $fu-shadow-sm;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.oc-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.oc-title {
  @include fu-font-display;
  margin: 0;
  font-size: 20px;
  font-weight: $fu-fw-semibold;
  color: $fu-text;
}

.oc-sub {
  margin: 2px 0 0;
  font-size: 13.5px;
  color: $fu-text-muted;
}

.oc-ghost {
  @include fu-button-reset;
  @include fu-focus-ring;
  flex: none;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1px solid $fu-border;
  background: $fu-surface;
  color: $fu-text-muted;
  display: grid;
  place-items: center;
  cursor: pointer;

  &:hover { color: $fu-text; }
}

.oc-hero {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 10px;
}

.oc-num {
  @include fu-tabular;
  font-size: 48px;
  font-weight: $fu-fw-semibold;
  letter-spacing: -0.03em;
  line-height: 1;
  color: $fu-text;
}

.oc-of {
  font-size: 14px;
  color: $fu-text-muted;
}

.oc-stats-note {
  margin: -4px 0 0;
  padding: 8px 12px;
  border-radius: 12px;
  background: $fu-surface-sunken;
  border: 1px solid $fu-border;
  font-size: 12.5px;
  line-height: 1.4;
  color: $fu-text-strong;
}

.oc-goal {
  display: grid;
  gap: 8px;
}

.oc-goal-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 2px 12px;
  font-size: 12.5px;
  color: $fu-text-muted;

  b { @include fu-tabular; color: $fu-text; font-weight: $fu-fw-semibold; }
}

.oc-goal-target { color: $fu-text-strong; font-weight: $fu-fw-medium; }

.oc-goal-track {
  height: 8px;
  border-radius: 999px;
  background: $fu-surface-sunken;
  border: 1px solid $fu-border;
  overflow: hidden;
}

.oc-goal-fill {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: $fu-brand-500;
  transition: width 0.3s;
}

.oc-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.oc-chip {
  @include fu-button-reset;
  @include fu-focus-ring;
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 6px;
  max-width: 100%;
  padding: 6px 12px;
  text-align: left;
  border-radius: 18px;
  border: 1px solid $fu-border;
  background: $fu-surface-sunken;
  font-size: 13px;
  color: $fu-text-strong;
  cursor: pointer;

  b { @include fu-tabular; color: $fu-text; }
  &:hover:not(:disabled) { border-color: $fu-border-strong; }
  &.is-open { background: $fu-brand-50; border-color: $fu-brand-200; color: $fu-brand-700; }
  &:disabled { cursor: default; opacity: 0.7; }
}

.oc-chip-k { color: $fu-text-muted; }
.oc-chip-v { color: $fu-brand-700; font-weight: $fu-fw-semibold; }
.oc-chip-caret { flex: none; }

.oc-list {
  border: 1px solid $fu-border;
  border-radius: 16px;
  padding: 12px 14px;
  background: $fu-surface;
}

.oc-list-title {
  margin: 0 0 6px;
  font-size: 12.5px;
  font-weight: $fu-fw-semibold;
  color: $fu-text-strong;
}

.oc-list-scroll {
  max-height: 220px;
  overflow: auto;

  table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
  th, td { padding: 5px 8px 5px 0; text-align: left; border-bottom: 1px solid $fu-border; }
  thead th { color: $fu-text-muted; font-weight: $fu-fw-medium; position: sticky; top: 0; background: $fu-surface; }
  tbody tr:last-child td { border-bottom: 0; }
}

.oc-body {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px 16px;
}

.oc-ring {
  position: relative;
  flex: 0 0 148px;
  width: 148px;
  max-width: 100%;
  margin: 0 auto;
}

.oc-svg {
  display: block;
  width: 100%;
  height: auto;
  overflow: visible;
}

.oc-petal {
  stroke-width: 5;
  stroke-linejoin: round;
  transition: opacity 0.15s;

  &.is-dim { opacity: 0.25; }
}

.oc-center {
  position: absolute;
  inset: 0;
  display: grid;
  place-content: center;
  text-align: center;
  padding: 0 22%;
  pointer-events: none;

  b {
    @include fu-tabular;
    font-size: 26px;
    font-weight: $fu-fw-semibold;
    letter-spacing: -0.03em;
    line-height: 1.05;
    color: $fu-text;
  }

  span {
    margin-top: 2px;
    font-size: 11px;
    line-height: 1.2;
    color: $fu-text-muted;
    overflow-wrap: anywhere;
  }
}

.oc-legend {
  flex: 1 1 200px;
  min-width: 0;
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 1px;
}

.oc-row {
  @include fu-focus-ring;
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr) auto 34px;
  align-items: center;
  gap: 8px;
  padding: 4px 6px;
  border-radius: 8px;
  font-size: 12.5px;
  color: $fu-text-strong;
  transition: opacity 0.15s;

  &:hover { background: $fu-surface-sunken; }
  &.is-dim { opacity: 0.35; }
  &.is-zero:not(.is-dim) { opacity: 0.5; }
  &.is-button { cursor: pointer; }
}

.oc-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  display: inline-block;
}

.oc-row-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.oc-row-more {
  margin-left: 6px;
  font-size: 11.5px;
  color: $fu-text-muted;
}

.oc-row-caret {
  margin-left: 4px;
  color: $fu-text-muted;
  transition: transform 0.15s;

  &.is-open { transform: rotate(180deg); }
}

.oc-row-n {
  @include fu-tabular;
  font-weight: $fu-fw-semibold;
  color: $fu-text;
}

.oc-row-p {
  @include fu-tabular;
  text-align: right;
  font-size: 12px;
  color: $fu-text-muted;
}

.oc-table {
  overflow-x: auto;

  table {
    @include fu-tabular;
    width: 100%;
    border-collapse: collapse;
    font-size: 12.5px;
  }

  th, td {
    padding: 6px 8px;
    border-bottom: 1px solid $fu-border;
    text-align: right;
  }

  th:first-child, td:first-child { text-align: left; }
  thead th { color: $fu-text-muted; font-weight: $fu-fw-medium; }
  tbody th { font-weight: $fu-fw-regular; color: $fu-text-strong; }

  .oc-folded {
    color: $fu-text-muted;

    th { padding-left: 22px; color: $fu-text-muted; }
  }
}
</style>
